import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { CookieConsent } from "./components/CookieConsent";
import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";

// Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import EnergitjekPage from "./pages/EnergitjekPage";
import CasesPage, { CaseDetailPage } from "./pages/CasesPage";
import ESGPage from "./pages/ESGPage";
import ContactPage from "./pages/ContactPage";
import JobsPage from "./pages/JobsPage";
import NybygPage from "./pages/NybygPage";
import NotFoundPage from "./pages/NotFoundPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";

// Scroll to top on route change with smooth behavior
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

// Google Analytics 4 page view tracking for SPA
function GAPageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-WH33T6S5KP', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
}

// Page transition wrapper
const PageWrapper = ({ children }) => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <motion.main
        key={location.pathname}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ 
          duration: 0.4,
          ease: [0.25, 0.1, 0.25, 1]
        }}
      >
        {children}
      </motion.main>
    </AnimatePresence>
  );
};

// Animated Routes component
function AnimatedRoutes() {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Client-side redirects for old WordPress URLs */}
        <Route path="/dilling-el" element={<Navigate to="/om-os" replace />} />
        <Route path="/dilling-el/*" element={<Navigate to="/om-os" replace />} />
        <Route path="/ledige-jobs" element={<Navigate to="/ledige-stillinger" replace />} />
        <Route path="/ledige-jobs/*" element={<Navigate to="/ledige-stillinger" replace />} />
        {/* Removed: <Route path="/kontakt/" element={<Navigate to="/kontakt" replace />} /> - causing route conflicts */}
        
        {/* Main routes */}
        <Route path="/" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <HomePage />
          </motion.div>
        } />
        <Route path="/om-os" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <AboutPage />
          </motion.div>
        } />
        <Route path="/ydelser/energitjek" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <EnergitjekPage />
          </motion.div>
        } />
        <Route path="/ydelser/:serviceId" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <ServiceDetailPage />
          </motion.div>
        } />
        <Route path="/cases" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <CasesPage />
          </motion.div>
        } />
        <Route path="/cases/:id" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <CaseDetailPage />
          </motion.div>
        } />
        <Route path="/esg" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <ESGPage />
          </motion.div>
        } />
        <Route path="/kontakt" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <ContactPage />
          </motion.div>
        } />
        <Route path="/ledige-stillinger" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <JobsPage />
          </motion.div>
        } />
        <Route path="/nybyg-og-renovering" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <NybygPage />
          </motion.div>
        } />
        <Route path="/privatlivspolitik" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <PrivacyPolicyPage />
          </motion.div>
        } />
        <Route path="*" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <NotFoundPage />
          </motion.div>
        } />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-[#F4F2EE]">
        <BrowserRouter>
          <ScrollToTop />
          <GAPageViewTracker />
          <Header />
          <AnimatedRoutes />
          <Footer />
          <CookieConsent />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
